import '../Main.scss';

import * as React from 'react';
import ReactInlineSvg from 'react-inlinesvg';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import {
    Button,
    Col,
    Row,
} from 'reactstrap';
import { ApplicationState } from '../../Store';
import {
    GetAlwaysCollectBorrowerBusinessInfo,
    GetDefaultSubmissionOptions,
    GetShowBorrowerIntroBanner,
} from '../../Store/AppSettings';
import {
    BorrowerActionCreators,
    GetBorrowerAnonymousState,
    GetBorrowerSafeWSDetails,
} from '../../Store/Borrower';
import {
    ConnectorType,
    actionCreators as ImportFinancialsActions,
    ImportMetadataKeys,
    LinkMode,
} from '../../Store/ImportFinancials';
import {
    GetCurrentDataCollectionSubmissionRequest,
    GetRequestedOptions,
    actionCreators as NewDataCollectionParametersActions,
    RetrieveShareableLinkOptionsFailed,
    RetrievingShareableLinkOptions,
} from '../../Store/NewDataCollectionParameters';
import {
    GetBasicSettings,
    GetTenantName,
    KnownTenants,
    TenantActionCreators,
} from '../../Store/Tenant';
import { GetWorkspaceIdFromUrl } from '../../Utils/PathUtils';
import { EnterWorkspaceInfo } from '../EnterWorkspaceInfo/EnterWorkspaceInfo';

import { AccountingPkgPresentation } from '../SelectAccountingSystem/AccountingPkgPresentation';
import { SelectAccountingSystem } from '../SelectAccountingSystem/SelectAccountingSystem';

import { PortalCreateSubmissionRequestOptions } from '../../Models/PortalCreateSubmissionRequestOptions';
import { RedirectConnectNavigator } from '../../Utils/ConnectWindow';

import { BorrowerPortalChildProps } from './BorrowerPortalV2';

import {
    EntitySafeDetailResponse,
    PortalTextBorrowerAnonymousSubmission,
    PortalTextBorrowerParameters,
    PortalTextCommon,
    PortalTextCommonImport,
    PortalTextLogin,
} from '../../Models/Api/strongbox.financialportal';
import { DefaultStrongboxInfoLink } from '../../Utils/Constants';
import { LogMessage, SeverityLevel } from '../../Utils/Logging';
import { MetricsService } from '../../Utils/Metrics';
import { ImportSecurityComforter } from '../Utils/ImportSecurityComforter';

import StrongboxLogo from '../../images/StrongboxLogo.svg';

import { GetWorkspaceSafeDetails } from '../../Services/WorkspaceService';

import { GetPortalText } from '../../Store/PortalAssets';
import { GetPortalBusyState } from '../../Store/UIState';

import { TenantUsesStrongboxWithLink } from '../Utils/TenantUsesStrongboxWithLink';

type InjectedReduxState = {
    anonymousLink: boolean;
    borrowerWsDetails: EntitySafeDetailResponse | undefined;
    shareableLinkOptions: PortalCreateSubmissionRequestOptions | undefined;
    retrievingShareableLinkOptions: boolean;
    retrievingSharedLinkOptionsFailed: boolean;
    dataCollectionSubmissionActive: boolean;
    defaultAnonymousSubmissionOptions: PortalCreateSubmissionRequestOptions;
    strongboxInfoLink: string;
    showIntroBanner: boolean;
    alwaysCollectBorrowerBusinessInfo: boolean;
    anonSubmissionText?: PortalTextBorrowerAnonymousSubmission;
    tenantDefault: KnownTenants.defaultLender | KnownTenants.defaultAccountant;
    portalIsBusy: boolean;
    lenderName: string;
    borrowerParametersText?: PortalTextBorrowerParameters;
    commonText?: PortalTextCommon;
    allTextIsRetrieved: boolean;
    allAnonymousTextIsRetrieved: boolean;
    loginText?: PortalTextLogin;
};

type InjectedActionCreators = typeof BorrowerActionCreators & typeof ImportFinancialsActions & typeof TenantActionCreators & typeof NewDataCollectionParametersActions;

type Props = InjectedReduxState & InjectedActionCreators & BorrowerPortalChildProps;

export const PortalSubmissionV2Component: React.FC<Props> = (props): React.ReactElement => {
    const {
        alwaysCollectBorrowerBusinessInfo,
        anonymousLink,
        borrowerId,
        borrowerWsDetails,
        importAccountingPkgAnonymous,
        retrievingShareableLinkOptions,
        retrievingSharedLinkOptionsFailed,
        SetBorrowerId,
        SetBorrowerAcceptedBusinessInfo,
        SetBorrowerAnonymous,
        shareableLinkOptions,
        showIntroBanner,
        working,
        anonSubmissionText,
        portalIsBusy,
        commonText,
        allTextIsRetrieved,
        allAnonymousTextIsRetrieved,
        SetBorrowerUniversalConnector,
        CreateUniversalConnectorSubmissionAndStartImport,
        dataCollectionSubmissionActive,
        importAccountingPkg,
    } = props;

    const location = useLocation();

    const [workspaceInfoValid, setWorkspaceInfoValid] = React.useState<boolean>(false);
    const [hasAcceptedWorkspaceInfo, setHasAcceptedWorkspaceInfo] = React.useState<boolean>(false);
    const [showingIntroBanner, setShowingIntroBanner] = React.useState<boolean>(false);
    const [collectBorrowerBusinessInfo, setCollectBorrowerBusinessInfo] = React.useState<boolean>(false);
    // Guards the borrower universal-collector auto-connect so it fires exactly once (it redirects the whole
    // page, but borrowerId/options can settle across a couple of renders before it can run).
    const borrowerUcStartedRef = React.useRef<boolean>(false);

    // static displayName = Home.name;

    const isValidBorrowerId = (borrowerId: string): boolean => {
        return (!!borrowerId) && (borrowerId.trim() !== '');
    }

    const setBorrowerIdWrapper = (id: string, wsDetails?: EntitySafeDetailResponse): void => {
        SetBorrowerId(id, wsDetails);
        if (!!id) {
            MetricsService.identifyUser('portal', id);
        }
    }

    const extractParameters = (): void => {
        const borrowerId = GetWorkspaceIdFromUrl(location.search);

        if (!isValidBorrowerId(borrowerId)) {
            SetBorrowerAnonymous(true);
            setBorrowerIdWrapper(borrowerId);
        } else {
            GetWorkspaceSafeDetails(borrowerId)
                .then(response => {
                    setBorrowerIdWrapper(borrowerId, response);
                })
                .catch(e => {
                    setBorrowerIdWrapper(borrowerId);
                });
        }
    }

    React.useEffect(() => {
        extractParameters();

        if (showIntroBanner) {
            setShowingIntroBanner(true);
        }

        // I want this to execute equivalent to componentDidMount so this is appropriate
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        const shouldCollect = anonymousLink || alwaysCollectBorrowerBusinessInfo
        setCollectBorrowerBusinessInfo(shouldCollect);
    }, [anonymousLink, alwaysCollectBorrowerBusinessInfo]);

    // Borrower package selection is handled by BorrowerPortalV2 (the parent): it gates on the
    // parameters/security modal and drives the shared connect flow, so the initial import here and the
    // "import additional financials" action on the Linked page share the exact same confirmation flow.
    const onSelectAccountingPackage = (accountingPkg: AccountingPkgPresentation): void => {
        if (anonymousLink) {
            // TODO(datacollection-refactor): the anonymous path still needs the workspace entity
            // created before a submission request can be made; left on the legacy callback for now.
            importAccountingPkgAnonymous(accountingPkg, workspaceInfoValid);
        } else {
            importAccountingPkg(accountingPkg);
        }
    }

    // A secure link created with "File Upload" carries the universal-collector marker in its metadata.
    const isUniversalCollectorLink = React.useMemo(() => {
        return !!shareableLinkOptions?.metadata?.some(m => m.id === ImportMetadataKeys.ucUniversalConnector);
    }, [shareableLinkOptions]);

    React.useEffect(() => {
        if ((!!shareableLinkOptions) && (!!shareableLinkOptions.metadata)) {
            SetBorrowerUniversalConnector(isUniversalCollectorLink);
        }

        // shareableLinkOptions/isUniversalCollectorLink is all I want here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shareableLinkOptions, isUniversalCollectorLink])

    // Borrower universal-collector auto-connect: a "File Upload" secure link shows no picker and no
    // confirmation — it goes straight through to the upload connector as though the borrower had chosen an
    // accounting system and clicked through the confirmation dialogs. We build the same direct request the
    // package-click path would (hasShareableLink: false, LinkMode.direct) and hand it to the shared universal-
    // collector thunk with a full-page RedirectConnectNavigator (no popup — the whole session is this upload).
    // Anonymous links still route through the legacy path, and we wait for a valid borrowerId + loaded options.
    //
    // Guards: the local ref stops re-runs of this instance, and dataCollectionSubmissionActive stops a SECOND
    // flow after a remount. BorrowerPortalV2 bumps its container key once init finishes, which unmounts and
    // remounts this subtree with a fresh ref — the store-state guard is what makes the auto-start idempotent
    // across that remount (the in-flight thunk survives the unmount and completes the redirect on its own).
    React.useEffect(() => {
        if (borrowerUcStartedRef.current) {
            return;
        }
        if (!isUniversalCollectorLink || anonymousLink || !isValidBorrowerId(borrowerId) || !shareableLinkOptions || dataCollectionSubmissionActive) {
            return;
        }

        borrowerUcStartedRef.current = true;

        const options: PortalCreateSubmissionRequestOptions = {
            ...shareableLinkOptions,
            hasShareableLink: false,
        };

        CreateUniversalConnectorSubmissionAndStartImport(
            {
                workspaceId: borrowerId,
                workspaceName: borrowerWsDetails?.displayName || '',
                linkMode: LinkMode.direct,
                connectorType: ConnectorType.universalConnector,
                options,
            },
            new RedirectConnectNavigator(),
            'borrower'
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUniversalCollectorLink, anonymousLink, borrowerId, borrowerWsDetails, shareableLinkOptions, dataCollectionSubmissionActive])

    const accountingPackageButtons = (tabIndexBase: number) => {
        // isWorking is set when the user has chosen to go to the end without linking with an accounting package.
        //
        // hasAcceptedWorkspaceInfo will be false if we are in anonymous mode and the user has not clicked to accept
        // the workspace info they entered. workspaceInfoValid will be true only if there is valid workspace information
        // in the entry fields, it can be false when hasAcceptedWorkspaceInfo is true and they changed the information.
        //
        // showingDialog will be true when the user has clicked to link an accounting package.  At that point an async
        // process to get the connection (that includes a delegatedAccessToken) is started.  connection will be undefined
        // for a small time between clicking the accounting package button and actually having the connection information
        // available.

        const buttonsDisabled =
            (working || retrievingShareableLinkOptions || retrievingSharedLinkOptionsFailed ||
                (hasAcceptedWorkspaceInfo && !workspaceInfoValid))

        return (
            <SelectAccountingSystem
                buttonsDisabled={buttonsDisabled}
                onLink={onSelectAccountingPackage}
                rowClass={'borrower-content-row'}
                tabIndexBase={tabIndexBase}
            />
        );
    }

    const setWorkspaceInputInvalid = (): void => {
        setWorkspaceInfoValid(false);
    }

    const setWorkspaceInputValid = (businessName: string, emailAddress: string): void => {
        setWorkspaceInfoValid(true);

        SetBorrowerAcceptedBusinessInfo(businessName, emailAddress);
    }

    const showBorrowerCollectionInfo = React.useMemo(() => {
        return (collectBorrowerBusinessInfo) && !(alwaysCollectBorrowerBusinessInfo && hasAcceptedWorkspaceInfo);
    }, [collectBorrowerBusinessInfo, alwaysCollectBorrowerBusinessInfo, hasAcceptedWorkspaceInfo]);

    const borrowerInfoTabStops = React.useMemo(() => {
        return showBorrowerCollectionInfo ? 2 : 0;
    }, [showBorrowerCollectionInfo])

    const commonContent = React.useMemo(() => {
        return (
            <React.Fragment>
                <Row className={'borrower-content-row'}>
                    <Col>
                        <h2>{anonSubmissionText?.selectAccountingSystem || ''}</h2>
                        { /*<h2>{tenantDefault === KnownTenants.defaultAccountant ? (anonSubmissionText?.submissionWelcomeAccountant || '') : (anonSubmissionText?.submissionWelcomeLender || '')}</h2>*/}
                    </Col>
                </Row>
                <Row className={'borrower-content-row'}>
                    <Col>
                        <TenantUsesStrongboxWithLink
                            msgTemplate={anonSubmissionText?.templateEntityUsesStrongbox || '{0}'}
                            learnMore={commonText?.learnMore || ''}
                            learnMoreLabel={anonSubmissionText?.ariaLabelLearnMore || ''}
                        />
                    </Col>
                </Row>
                <Row
                    className={'borrower-content-row borrower-content-list-row'}
                >
                    <Col>
                        {accountingPackageButtons(borrowerInfoTabStops + 1)}
                    </Col>
                </Row>
                <Row className={'borrower-content-row'}>
                    <Col>
                        <ImportSecurityComforter />
                    </Col>
                </Row>
            </React.Fragment>
        )

        // accountingPackageButtons reads working/retrieving state and (in the anonymous flow)
        // hasAcceptedWorkspaceInfo + workspaceInfoValid to decide whether the package buttons are enabled and
        // which handler runs. These MUST be in the dependency list or the memo renders stale buttons — e.g. the
        // anonymous package buttons stay disabled with a stale workspaceInfoValid and clicking does nothing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shareableLinkOptions, retrievingSharedLinkOptionsFailed, retrievingShareableLinkOptions, working, hasAcceptedWorkspaceInfo, workspaceInfoValid, borrowerInfoTabStops])

    const startImportFragment = React.useMemo(() => {
        if ((showingIntroBanner) || (portalIsBusy)) {
            return undefined;
        }

        return (
            <React.Fragment>
                {
                    !showBorrowerCollectionInfo && (
                        <React.Fragment>
                            {commonContent}
                        </React.Fragment>
                    )
                }
                {
                    showBorrowerCollectionInfo && !portalIsBusy && (
                        <>
                            <Row className={'borrower-content-row'}>
                                <Col>
                                    <div className={'p2'}>
                                        <h2>{anonSubmissionText?.submissionEnterBusinessInfo}</h2>
                                    </div>
                                </Col>
                            </Row>
                            <Row className={'borrower-content-row'}>
                                <Col
                                    style={{
                                        marginTop: '30px',
                                    }}
                                >
                                    <EnterWorkspaceInfo
                                        onInputValid={setWorkspaceInputValid}
                                        onInputInvalid={setWorkspaceInputInvalid}
                                        autoFocus={true}
                                        tabIndexBase={1}
                                        anonSubmissionText={anonSubmissionText}
                                    />
                                </Col>
                            </Row>
                            {!hasAcceptedWorkspaceInfo && (
                                <Row className={'borrower-content-row'}>
                                    <Col>
                                        <Button
                                            type="button"
                                            className={`small regular-button`}
                                            disabled={!workspaceInfoValid || working}
                                            onClick={() => {
                                                LogMessage(
                                                    'User has entered business information in borrower flow introduction page and chosen to continue',
                                                    SeverityLevel.Information
                                                );
                                                setHasAcceptedWorkspaceInfo(true);
                                            }}
                                            style={{
                                                marginTop: '20px',
                                            }}
                                            tabIndex={0}
                                            color="primary"
                                        >
                                            {anonSubmissionText?.continue || ''}
                                        </Button>
                                    </Col>
                                </Row>
                            )}
                            {hasAcceptedWorkspaceInfo && (
                                <React.Fragment>
                                    <Row className={'extra-row-spacing-medium'}></Row>
                                    {commonContent}
                                </React.Fragment>
                            )}
                        </>
                    )
                }
            </React.Fragment>
        );

        // This is the dependency list I actually want
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showingIntroBanner, workspaceInfoValid, working, hasAcceptedWorkspaceInfo, collectBorrowerBusinessInfo]);

    const bannerFragment = React.useMemo(() => {
        if (!showingIntroBanner) {
            return undefined;
        }
        return (
            <div className={'control-region'}>
                <Row className={'borrower-content-row'}>
                    <Col>
                        <ReactInlineSvg
                            src={StrongboxLogo}
                            className={'strongbox-logo'}
                        />
                    </Col>
                </Row>
                <Row
                    className={'borrower-content-row intro-banner-content'}
                    style={{
                        marginTop: '9%',
                    }}
                >
                    <Col>
                        <div className={'p2'}>
                            <h2>{anonSubmissionText?.submissionIntroBannerText || ''}</h2>
                        </div>
                    </Col>
                </Row>
                <Row
                    className={'borrower-content-row'}
                    style={{
                        marginTop: '20%'
                    }}
                >
                    <Col>
                        <Button
                            onClick={() => {
                                LogMessage(`User advancing past introduction banner in borrower flow`, SeverityLevel.Information);
                                setShowingIntroBanner(false);
                            }}
                            className={'focus-button'}
                            autoFocus={true}
                            tabIndex={1}
                            color="primary"
                        >
                            {anonSubmissionText?.continueToStrongbox || ''}
                        </Button>
                    </Col>
                </Row>
            </div>
        )

        // This is the dependency list I actually want
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showingIntroBanner]);

    if (!showBorrowerCollectionInfo && !allTextIsRetrieved) {
        return (<></>);
    }
    else if (showBorrowerCollectionInfo && !allAnonymousTextIsRetrieved) {
        return (<></>);
    }

    // The connect flow (parameters gate + confirm/connect/working/error modals) and the redirect on import
    // start are owned by BorrowerPortalV2 (the parent), so they're shared with the Linked page's additional
    // import. This page only renders the borrower's package picker / intro. For a universal-collector link
    // nothing is rendered here — the parent's flow shows the working spinner and drives the redirect.
    return (
        <React.Fragment>
            {!isUniversalCollectorLink && (
                <React.Fragment>
                    {!!startImportFragment && startImportFragment}
                    {!!bannerFragment && bannerFragment}
                </React.Fragment>
            )}
        </React.Fragment>
    )
}

export const PortalSubmissionV2 = connect<InjectedReduxState, InjectedActionCreators, BorrowerPortalChildProps, ApplicationState>(
    (appState: ApplicationState) => {
        const anonymousLink = GetBorrowerAnonymousState(appState);
        const basicSettings = GetBasicSettings(appState);

        const commonImportText = GetPortalText<PortalTextCommonImport>(appState, 'commonImport');

        const result: InjectedReduxState = {
            anonymousLink,
            borrowerWsDetails: GetBorrowerSafeWSDetails(appState),
            shareableLinkOptions: GetRequestedOptions(appState),
            retrievingShareableLinkOptions: RetrievingShareableLinkOptions(appState),
            retrievingSharedLinkOptionsFailed: RetrieveShareableLinkOptionsFailed(appState),
            dataCollectionSubmissionActive: !!GetCurrentDataCollectionSubmissionRequest(appState),
            defaultAnonymousSubmissionOptions: GetDefaultSubmissionOptions(appState, 'shareableanonymous', true, commonImportText),
            strongboxInfoLink: (basicSettings && basicSettings.StrongboxSecurityInfoLink) || DefaultStrongboxInfoLink,
            showIntroBanner: GetShowBorrowerIntroBanner(appState),
            alwaysCollectBorrowerBusinessInfo: GetAlwaysCollectBorrowerBusinessInfo(appState),
            anonSubmissionText: GetPortalText<PortalTextBorrowerAnonymousSubmission>(appState, 'borrowerAnonymousSubmission'),
            tenantDefault: basicSettings?.defaultResource || KnownTenants.defaultAccountant,
            portalIsBusy: GetPortalBusyState(appState),
            lenderName: GetTenantName(appState),
            borrowerParametersText: GetPortalText<PortalTextBorrowerParameters>(appState, 'borrowerParameters'),
            commonText: GetPortalText<PortalTextCommon>(appState, 'commonText'),
            loginText: GetPortalText<PortalTextLogin>(appState, 'loginPage'),
            allTextIsRetrieved: false,
            allAnonymousTextIsRetrieved: false,
        };

        result.allTextIsRetrieved =
            !!result.anonSubmissionText && !!result.borrowerParametersText && !!result.commonText && !!result.loginText;
        result.allAnonymousTextIsRetrieved = !!result.anonSubmissionText && !!result.loginText;

        return result;
    },
    dispatch => bindActionCreators(
        {
            ...BorrowerActionCreators,
            ...ImportFinancialsActions,
            ...TenantActionCreators,
            ...NewDataCollectionParametersActions,
        },
        dispatch
    )
)(PortalSubmissionV2Component);
